import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { formatPhoneNumber } from "../utils/phoneFormat";
import { schema } from "../schemas/carrers.schema";
import { CareersFormData, JobApiItem } from "../types";
import { useEmailJs } from "./useEmailJs";
import { routes } from "../config/routes";

export function useCareersForm() {
  const [vacancy, setVacancy] = useState<JobApiItem>();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");

  const { id } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { sendConfirmationEmailToHR } = useEmailJs();
  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
    setValue,
    watch,
    reset,
  } = useForm<CareersFormData>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    defaultValues: {
      names: "",
      lastNames: "",
      email: "",
      phone: "",
      direction: "",
      linkedin: "",
      portfolio: "",
    },
  });

  // Format phone
  const watchedPhone = watch("phone");
  useEffect(() => {
    if (watchedPhone) {
      const formatted = formatPhoneNumber(watchedPhone);
      if (formatted !== watchedPhone) {
        setValue("phone", formatted, { shouldValidate: true });
      }
    }
  }, [watchedPhone, setValue]);

  // Current date - formato ISO para Strapi
  const formattedDate = useMemo(() => {
    return new Date().toISOString();
  }, []);

  // Fetch vacancy
  useEffect(() => {
    fetch(
      `${import.meta.env.VITE_API_BASE_URL}${routes.jobDetails.replace(
        ":id",
        id
      )}`
    )
      .then((response) => response.json())
      .then((data) => {
        setVacancy(data.data);
        setIsLoading(false);
      })
      .catch((error) => {
        throw new Error(error);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFileError("");
    } else {
      setSelectedFile(null);
      setFileError("Resume es requerido");
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setFileError("");
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const uploadFileToStrapi = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("files", file);

    const uploadUrl = `${import.meta.env.VITE_API_BASE_URL}${routes.upload}`;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setProgress(percentComplete);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            // Strapi returns an array of uploaded files
            if (Array.isArray(response) && response.length > 0) {
              const uploadedFile = response[0];
              // Strapi returns the file with url property
              const fileUrl = uploadedFile.url;
              if (fileUrl) {
                // If URL is relative, make it absolute
                const absoluteUrl = fileUrl.startsWith("http")
                  ? fileUrl
                  : `${import.meta.env.VITE_API_BASE_URL}${fileUrl}`;
                resolve(absoluteUrl);
              } else {
                reject(new Error("No URL returned from Strapi"));
              }
            } else {
              reject(new Error("Invalid response from Strapi"));
            }
          } catch (error) {
            reject(new Error("Error parsing Strapi response"));
          }
        } else {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during upload"));
      });

      xhr.open("POST", uploadUrl);
      xhr.send(formData);
    });
  };

  const onSubmit = async (data: CareersFormData) => {
    if (!selectedFile) {
      setFileError("Resume es requerido");
      return;
    }

    setIsLoading(true);
    setProgress(0);

    try {
      // 1. Upload file to Strapi
      const fileUrl = await uploadFileToStrapi(selectedFile);

      // 2. Prepare submission data
      // IMPORTANTE: Los nombres de los campos deben coincidir EXACTAMENTE con Strapi
      // Strapi es case-sensitive. Verifica en Content-Type Builder los nombres exactos.
      const submitData = {
        data: {
          Nombres: data.names,
          Apellidos: data.lastNames,
          Email: data.email,
          Telefono: data.phone,
          Direccion: data.direction,
          Linkedin: data.linkedin,
          ...(data.portfolio && { Portafolio: data.portfolio }),
          Resume: fileUrl,
          Vacante_Aplicada: vacancy?.jobTitle || "",
          Fecha: formattedDate,
        },
      };

      console.log(
        "Enviando datos a Strapi:",
        JSON.stringify(submitData, null, 2)
      );

      // 3. Submit to CMS
      const url = `${import.meta.env.VITE_API_BASE_URL}${routes.talentBank}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response from Strapi:", errorText);
        let errorMessage = `Error del servidor: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error?.message) {
            errorMessage = errorJson.error.message;
          } else if (errorJson.error?.details?.errors) {
            errorMessage = errorJson.error.details.errors
              .map((err: any) => err.message || err.path)
              .join(", ");
          }
        } catch (e) {
          // If parsing fails, use the text as is
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // 4. Send confirmation email (non-blocking)
      try {
        await sendConfirmationEmailToHR(
          vacancy?.jobTitle || "",
          data.names,
          data.lastNames
        );
      } catch (emailError) {
        console.warn("Error enviando email de confirmación:", emailError);
        // Don't fail the whole process if email fails
      }

      // 5. Show success message
      setIsLoading(false);

      await Swal.fire({
        title: "¡Gracias por aplicar!",
        text: "Tu solicitud ha sido enviada exitosamente.",
        icon: "success",
        confirmButtonText: "Continuar",
        timer: 3000,
        timerProgressBar: true,
      });

      // 6. Navigate to success page with job title
      const jobTitle = vacancy?.jobTitle || "vacante";
      navigate(`/success/${encodeURIComponent(jobTitle)}`, {
        replace: true,
      });

      // 7. Reset form state
      reset();
      setSelectedFile(null);
      setProgress(0);
      setFileError("");
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    } catch (error) {
      setIsLoading(false);
      setProgress(0);

      console.error("Error en aplicación:", error);

      let errorMessage = "Error inesperado. Intente nuevamente.";

      if (error instanceof Error) {
        if (error.message.includes("Error del servidor")) {
          errorMessage =
            "Error del servidor. Verifique su conexión e intente nuevamente.";
        } else if (
          error.message.includes("upload") ||
          error.message.includes("Upload")
        ) {
          errorMessage =
            "Error al subir el archivo. Verifique su conexión e intente nuevamente.";
        }
      }

      await Swal.fire({
        title: "¡Error al enviar aplicación!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "Reintentar",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
      });
    }
  };

  const getInputClass = (fieldName: keyof CareersFormData) => {
    const hasError = errors[fieldName];
    const isTouched = touchedFields[fieldName];

    if (!isTouched) return "border-b-black";
    if (hasError) return "border-b-red-500";
    return "border-b-green-600";
  };

  return {
    // RHF
    register,
    handleSubmit,
    errors,
    isValid,
    // Refs
    inputRef,
    formRef,
    // State
    selectedFile,
    fileError,
    progress,
    isLoading,
    // Handlers
    handleFileInput,
    handleDrop,
    handleDragOver,
    onSubmit,
    // Utils
    getInputClass,
  };
}
